import React, {useState, useEffect} from 'react'
import {SearchInput, SEARCH_INPUT_TYPE} from '../SearchInput/SearchInput'
import {_L, useLocaleState} from '../../context/LocaleContext'
import {AutoSizer, List} from 'react-virtualized'
import PromptRow from './PromptRow/PromptRow'
import Spinner from '../Spinner/Spinner'
import styled from 'styled-components'
import {
  fetchFloorPrompt,
  fetchBuildingPrompt,
  fetchRoomPrompt,
  fetchEquipmentPrompt,
  fetchEmployeePrompt,
  fetchDepartmentPrompt
} from '../../api/search'

export const BUILDING = 'BUILDING'
export const FLOOR = 'FLOOR'
export const ROOM = 'ROOM'
export const EQUIPMENT = 'EQUIPMENT'
export const EMPLOYEE = 'EMPLOYEE'
export const DEPARTMENT = 'DEPARTMENT'

const Prompt = props => {
  const {
    type,
    parents,
    onSelect,
    handleClose,
    onClearPrompt,
    placeholder,
    selectors,
    parentFields,
    parentRect,
    title
  } = props

  const [searchValue, setSearchValue] = useState('')
  const [searchResult, setSearchResult] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const localeState = useLocaleState()

  const getParentRestrictionValues = ({parents, parentFields, selectors}) => {
    let values = {}
    if (parents.length) {
      values = parents.reduce((acc, parent, idx) => {
        let parentField = parentFields[idx]
        acc[parentField] = selectors[parent].value
        return acc
      }, {})
    }

    return values
  }

  useEffect(
    () => {
      if (type === 'BUILDING') {
        setIsLoading(true)
        fetchBuildingPrompt({searchValue}).then(data => {
          setIsLoading(false)
          setSearchResult(data)
        })
      }
      if (type === 'FLOOR') {
        let {blId} = getParentRestrictionValues({
          parents,
          parentFields,
          selectors
        })

        setIsLoading(true)
        fetchFloorPrompt({blId, searchValue}).then(data => {
          setIsLoading(false)
          setSearchResult(data)
        })
      }

      if (type === 'ROOM') {
        let {blId, flId} = getParentRestrictionValues({
          parents,
          parentFields,
          selectors
        })

        setIsLoading(true)
        fetchRoomPrompt({blId, flId, searchValue}).then(data => {
          setIsLoading(false)
          setSearchResult(data)
        })
      }

      if (type === 'EQUIPMENT') {
        let {blId, flId, rmId} = getParentRestrictionValues({
          parents,
          parentFields,
          selectors
        })

        setIsLoading(true)
        fetchEquipmentPrompt({blId, flId, rmId, searchValue}).then(data => {
          setIsLoading(false)
          setSearchResult(data)
        })
      }

      if (type === 'EMPLOYEE') {
        setIsLoading(true)
        fetchEmployeePrompt({searchValue}).then(data => {
          setIsLoading(false)
          setSearchResult(data)
        })
      }

      if (type === 'DEPARTMENT') {
        setIsLoading(true)
        fetchDepartmentPrompt({searchValue}).then(data => {
          setIsLoading(false)
          setSearchResult(data)
        })
      }
    },
    [searchValue, parentFields, parents, type, selectors]
  )

  const onSearchChange = e => {
    let searchValue = e.target.value
    setSearchValue(searchValue)
  }

  const onRowSelect = item => onSelect(item)

  const onClose = evt => {	  
	    if(evt.key == "Enter" || evt.key == " " || !evt.key){ handleClose() }
	  }

  const onClear = evt => {
	  if(evt.key == "Enter" || evt.key == " " || !evt.key){ onClearPrompt({promptType: type}) }
  }

  const rowRenderer = ({index, key, style}) => {
    let item = searchResult[index]

    return (
      <PromptRow
        key={key}
        style={style}
        item={item}
        onClick={onRowSelect}
        type={type}
      />
    )
  }
  let formResultCount = searchResult.length + " results"
  return (
    <Container parentRect={parentRect} data-testid={'prompt-container'} role="dialog" aria-modal="true" aria-labelledby="title-section">
      {title && <Title id="title-section">{title}</Title>}
      <SearchContainer>
        <SearchInput
          type={SEARCH_INPUT_TYPE.CONTENT}
          onSearchChange={onSearchChange}
          value={searchValue}
          placeholder={placeholder}
          style={{
            container: {
              borderRadius: '4px',
              backgroundColor: '#F7F8FC'
            }
          }}
        />
      </SearchContainer>
      <PromptContainer>
        {isLoading ? (
          <SpinnerContainer>
            <Spinner color="#3395fe" />
          </SpinnerContainer>
        ) : (
          <AutoSizer>
            {({width, height}) => (
            <StyledList>
	            <List
	                id="list"
	                height={height}
	                overscanRowCount={20}
	                rowCount={searchResult.length}
	                rowHeight={80}
	                rowRenderer={rowRenderer}
	                width={width}
	                tabIndex={null}
	              />
	            <FormSearchStatus role="status" id="form-status">{formResultCount} </FormSearchStatus>
	        </StyledList>
            )}
          </AutoSizer>
        )}
      </PromptContainer>
      <div>
        <GroupButton>
          <ClearButton onClick={onClear} onKeyDown={onClear}>
            {_L({key: 'x Clear', localeState})}
          </ClearButton>
          <CloseButton onClick={onClose} onKeyDown={onClose}>
            {_L({key: 'Close', localeState})}
          </CloseButton>
        </GroupButton>
      </div>
    </Container>
  )
}

export default Prompt

const Container = styled.div`
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  z-index: 20;
  background: #ffffff;
  box-shadow: 0 2px 8px 0 rgba(0, 0, 0, 0.05);
  width: 700px;
  height: 560px;

  @media (max-width: 768px) {
    width: 100%;
    height: 100%;
    border-radius: 0;
  }
`

const Title = styled.h2`
  font-style: normal;
  font-weight: 600;
  font-size: 22px;
  color: #000000;
  padding: 23px 10px 7px 25px;
  display: flex;
  justify-content: flex-start;
`

const SpinnerContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: center;
  align-items: center;
`

const PromptContainer = styled.div`
  flex: 1;
  padding: 6px;
  box-sizing: border-box;
`
const StyledList = styled.ul`
  list-style-type:none;
`

const SearchContainer = styled.div`
  padding: 20px;
  height: 100px;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  @media (max-width: 420px) {
    height: 76px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }
`

const GroupButton = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 29px;
  height: 49px;
  border-top: 1px solid #eff0f4;
`

const StyledButton = styled.button`
  font-size: 14px;
  font-weight: 600;
  box-sizing: border-box;
  background: none;
  border: none;
  outline: none;
  cursor: pointer;
  border: 3px solid #FFFFFF;
  padding: 10px;
  margin-bottom: 3px;
  
  :focus { 
  	border: 3px solid #FF9900;
  }
`

const ClearButton = styled(StyledButton)`
  color: #3e4044;
  margin-right: 3px;
`

const CloseButton = styled(StyledButton)`
  color: #006ee5;
  margin-left: 3px;
`

const FormSearchStatus = styled.span`
	position: absolute;
	height: 1px;
	width: 1px;
	overflow: hidden;
	clip: rect(1px, 1px, 1px, 1px);
	text-indent: -100em; 
	white-space: nowrap;
`
